// components/ChatDock.tsx — V29 S1: the opaque "Chiedi al Prof" dock button.
//
// Replaces SwipeHint (the transparent gradient bar that collided with page
// text — audit A1). An OPAQUE ink pill floating bottom-right on chat-enabled
// screens (flow.chat), above the screens' reserved bottom padding
// ([data-has-chat] clearance in global.css). Opens the same GlobalChatSheet.
// Hidden while the sheet is open. Same one-affordance contract as SwipeHint:
// tapping this is the only shell-level way the sheet opens.

import type { JSX } from 'react';
import { useScreenFlow, type FlowKey } from '../state/screen-flow';
import { useChatSheet } from '../state/chat-sheet';
import { useTokens } from '../state/theme';
import { TABBAR_H, READING_DOCK_H } from '../tokens';

// Screens that pin their own reading dock at the bottom — the chat pill
// floats ABOVE it there (V29 S3 lesson dock; S4 adds the teoria reader).
const DOCKED_FLOWS: ReadonlySet<FlowKey> = new Set(['lesson-guidata', 'appendice-theory']);

export function ChatDock(): JSX.Element | null {
  const flow = useScreenFlow();
  const { isOpen, open } = useChatSheet();
  const tokens = useTokens();

  if (!flow.chat || isOpen) return null;

  // DP3: the TabBar is permanent, and the ChatDock is a SIBLING of the padded
  // clearance container — it must clear the bar explicitly (the reading docks
  // live inside the clearance and lift automatically).
  const bottomClearance = DOCKED_FLOWS.has(flow.key)
    ? `calc(${TABBAR_H}px + ${READING_DOCK_H}px + 14px + var(--sai-bottom))`
    : `calc(${TABBAR_H}px + 14px + var(--sai-bottom))`;

  return (
    <button
      type="button"
      data-testid="chat-dock"
      aria-label="Apri la chat con Il Prof"
      onClick={open}
      style={{
        position:       'absolute',
        right:          14,
        bottom:         bottomClearance,
        zIndex:         45,
        display:        'inline-flex',
        alignItems:     'center',
        gap:            8,
        minHeight:      44,
        padding:        '0 16px 0 13px',
        border:         'none',
        borderRadius:   999,
        background:     tokens.text,
        color:          tokens.bg,
        fontFamily:     tokens.fonts.body,
        fontSize:       13.5,
        fontWeight:     700,
        letterSpacing:  -0.1,
        cursor:         'pointer',
        boxShadow:      '0 10px 26px rgba(26, 21, 53, 0.32)',
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width={18}
        height={18}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      Chiedi al Prof
    </button>
  );
}
